import React from 'react';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import {
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
} from '@material-ui/icons';

// Layout

export const ColGroup = ({ columnWidths }) => (
  <colgroup>
    {columnWidths.map((width, i) => (
      <col key={`col-width-${i}-${width}`} style={{ width }} />
    ))}
  </colgroup>
);

const PaginationBox = styled(Box)`
  display: flex;
  padding: 1rem 0;
  justify-content: center;
  align-items: center;
`;

const SearchBox = styled(Box)`
  margin: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Collapsible Table

export const CollapsibleRowHeaderCell = ({ children, colSpan }) => (
  <TableCell colSpan={colSpan} style={{ borderBottom: 0 }}>
    {children}
  </TableCell>
);

export const CollapsibleRowBodyCell = ({ children, colSpan }) => (
  <TableCell colSpan={colSpan || 1} style={{ paddingBottom: '4rem' }}>
    {children}
  </TableCell>
);

export const CollapsibleSectionTableHeading = ({ heading, widthCount, colStart, colSpan }) =>
  new Array(widthCount).fill({}).map((x, i) =>
    i === colStart - 1 ? (
      <CollapsibleRowHeaderCell colSpan={colSpan || 1}>
        <Typography variant="h6" component="div">
          {heading}
        </Typography>
      </CollapsibleRowHeaderCell>
    ) : (
      <CollapsibleRowHeaderCell />
    )
  );

export const CollapsibleDrawerTable = ({ children, isExpanded, columnWidths }) => (
  <TableRow>
    <TableCell style={{ padding: 0, border: 0 }} colSpan={12}>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box style={{ paddingTop: '2rem' }}>
          <MuiTable>
            <ColGroup columnWidths={columnWidths} />
            <TableBody>{children}</TableBody>
          </MuiTable>
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

export const SearchableTable = ({
  TableRowComponent,
  columns,
  results,
  onSave,
  isSaving,
  isLoading,
  sortByFunction,
  searchField,
  paginationControls,
}) => {
  const columnWidths = columns.map(c => c.width);
  const headerLabels = columns.map(c => c.label);

  const tableHead = (
    <TableHead>
      <TableRow>
        {headerLabels.map((label, i) => (
          <TableCell key={`header-label-${i}-${label}`}>{label}</TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const tableBody = (
    <TableBody>
      {orderBy(results, [sortByFunction], ['asc']).map(rowData => (
        <TableRowComponent isSaving={isSaving} onSave={onSave} key={rowData?.id} {...rowData} />
      ))}
    </TableBody>
  );

  return (
    <TableContainer component={Paper}>
      {searchField && <SearchBox>{searchField}</SearchBox>}
      <MuiTable>
        <ColGroup columnWidths={columnWidths} />
        {tableHead}
        {tableBody}
      </MuiTable>
      <PaginationBox>{isLoading ? <CircularProgress size={'2.25rem'} /> : paginationControls}</PaginationBox>
    </TableContainer>
  );
};

// Controls

export const PaginationControls = ({ onBack, onForward, pageIndex = 0, totalPages, ...props }) => (
  <Box display="flex" flexDirection="row" alignItems="center" {...props}>
    <Button disabled={pageIndex === 0} onClick={onBack}>
      <ArrowLeftIcon />
    </Button>
    <Typography variant="caption">{`${pageIndex + 1} of ${totalPages}`}</Typography>
    <Button disabled={pageIndex === totalPages - 1} onClick={onForward}>
      <ArrowRightIcon />
    </Button>
  </Box>
);

export const RowActionIcons = ({ isEditing, isSaving, onEdit, onSave, onCancel, isExpanded, toggleIsExpanded }) => (
  <Box sx={{ display: 'inline-flex' }}>
    {!isEditing ? (
      <IconButton disabled={isSaving} style={{ marginLeft: '2.5rem' }} icon="edit" onClick={onEdit}>
        <EditIcon />
      </IconButton>
    ) : (
      <>
        <IconButton disabled={isSaving} icon="save" onClick={onSave}>
          <SaveIcon />
        </IconButton>
        <IconButton disabled={isSaving} icon="cancel" onClick={onCancel}>
          <CancelIcon />
        </IconButton>
      </>
    )}
    <IconButton aria-label="expand row" onClick={toggleIsExpanded} tabIndex={-1}>
      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  </Box>
);
